import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"

/**
 * SearchFiltersController - Search filter bar with sort, filter panel, and URL management
 *
 * UX Patterns:
 * - Hick's Law: Progressive disclosure of filter options
 * - Fitts' Law: Large touch targets for mobile
 * - Miller's Law: Grouped filters (category, price, condition)
 *
 * Targets:
 * - panel: Collapsible filter panel container
 * - toggleIcon: Icon that rotates when panel opens
 * - form: Filter form element
 * - sortInput: Hidden input for sort value
 * - priceMin/priceMax: Price range inputs
 * - customPriceInputs: Container for custom price inputs
 *
 * Usage:
 * <div data-controller="search-filters" data-search-filters-open-class="rotate-180">
 *   <button data-action="click->search-filters#togglePanel">Filters</button>
 *   <div data-search-filters-target="panel" class="hidden">...</div>
 * </div>
 */
const log = createLogger("SearchFilters")

export default class extends Controller {
  static targets = [
    "panel",
    "toggleIcon",
    "form",
    "sortInput",
    "priceMin",
    "priceMax",
    "customPriceInputs"
  ]

  static classes = ["open"]

  static values = {
    debounce: { type: Number, default: 300 }
  }

  connect() {
    log.debug("connect")
    this.debounceTimer = null
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  /**
   * Toggle the filter panel visibility
   */
  togglePanel(event) {
    event.preventDefault()
    const panel = this.panelTarget
    const isHidden = panel.classList.contains("hidden")

    if (isHidden) {
      panel.classList.remove("hidden")
      this.updateToggleIcon(true)
      this.updateAriaExpanded(event.currentTarget, true)
    } else {
      panel.classList.add("hidden")
      this.updateToggleIcon(false)
      this.updateAriaExpanded(event.currentTarget, false)
    }
  }

  /**
   * Apply sort option from dropdown selection
   * Called via dropdown:change event or direct click
   */
  applySort(event) {
    event.preventDefault()

    // Get sort value from clicked button
    const sortValue = event.currentTarget.dataset.sortValue ||
                      event.detail?.value

    if (!sortValue) {
      log.warn("No sort value found")
      return
    }

    log.debug("Apply sort:", sortValue)

    // Update hidden sort input
    if (this.hasSortInputTarget) {
      this.sortInputTarget.value = sortValue
    }

    // Submit form to apply sort
    this.submitForm()
  }

  /**
   * Set price preset and submit
   */
  setPricePreset(event) {
    event.preventDefault()
    const button = event.currentTarget
    const min = button.dataset.priceMin
    const max = button.dataset.priceMax

    log.debug("Set price preset:", { min, max })

    // Update price inputs
    if (this.hasPriceMinTarget) {
      this.priceMinTarget.value = min
    }
    if (this.hasPriceMaxTarget) {
      this.priceMaxTarget.value = max
    }

    // Hide custom price inputs if visible
    if (this.hasCustomPriceInputsTarget) {
      this.customPriceInputsTarget.classList.add("hidden")
    }

    // Submit form with debounce
    this.debouncedSubmit()
  }

  /**
   * Toggle custom price inputs visibility
   */
  toggleCustomPrice(event) {
    event.preventDefault()

    if (!this.hasCustomPriceInputsTarget) return

    const isHidden = this.customPriceInputsTarget.classList.contains("hidden")

    if (isHidden) {
      this.customPriceInputsTarget.classList.remove("hidden")
      // Focus first input
      if (this.hasPriceMinTarget) {
        this.priceMinTarget.focus()
      }
    } else {
      this.customPriceInputsTarget.classList.add("hidden")
    }
  }

  /**
   * Remove a specific filter and resubmit
   */
  removeFilter(event) {
    event.preventDefault()
    const button = event.currentTarget
    const filterType = button.dataset.filterType
    const filterValue = button.dataset.filterValue

    log.debug("Remove filter:", { filterType, filterValue })

    if (!this.hasFormTarget) return

    switch (filterType) {
      case "district":
        this.removeDistrictValue(filterValue)
        break
      case "price":
        this.clearFormField("price_min")
        this.clearFormField("price_max")
        break
      default:
        this.clearFormField(filterType)
    }

    // Submit form to apply changes
    this.submitForm()
  }

  /**
   * Clear all filters and resubmit
   */
  clearAll(event) {
    event.preventDefault()

    log.debug("Clear all filters")

    if (!this.hasFormTarget) return

    // Clear all filter fields (preserve query and sort)
    this.clearAllDistrictValues()
    this.clearFormField("price_min")
    this.clearFormField("price_max")

    // Submit form to apply changes
    this.submitForm()
  }

  /**
   * Remove a specific district hidden field
   */
  removeDistrictValue(districtId) {
    const hiddenField = this.formTarget.querySelector(
      `input[name="district_ids[]"][value="${districtId}"]`
    )
    if (hiddenField) {
      hiddenField.remove()
    }
  }

  /**
   * Clear all district hidden fields
   */
  clearAllDistrictValues() {
    const hiddenFields = this.formTarget.querySelectorAll(
      'input[name="district_ids[]"]'
    )
    hiddenFields.forEach(field => field.remove())
  }

  // ==================
  // Private Methods
  // ==================

  updateToggleIcon(isOpen) {
    if (!this.hasToggleIconTarget) return

    if (isOpen) {
      this.toggleIconTarget.classList.add(...this.openClasses)
    } else {
      this.toggleIconTarget.classList.remove(...this.openClasses)
    }
  }

  updateAriaExpanded(button, expanded) {
    button.setAttribute("aria-expanded", expanded.toString())
  }

  submitForm() {
    if (!this.hasFormTarget) {
      log.warn("No form target found")
      return
    }

    // Ensure Turbo uses replace action for filter changes (defensive)
    this.formTarget.dataset.turboAction = "replace"

    // Use requestSubmit for proper form submission with Turbo
    this.formTarget.requestSubmit()
  }

  debouncedSubmit() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    this.debounceTimer = setTimeout(() => {
      this.submitForm()
    }, this.debounceValue)
  }

  clearFormField(fieldName) {
    const field = this.formTarget.querySelector(`[name="${fieldName}"]`)
    if (field) {
      if (field.tagName === "SELECT") {
        field.selectedIndex = 0
      } else {
        field.value = ""
      }
    }
  }

  get openClasses() {
    return this.hasOpenClass ? this.openClass.split(" ") : ["rotate-180"]
  }
}
